import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/hooks/useCart";

const productCategories = [
  { id: "power-tools", name: "Power Tools", icon: "ri-flashlight-line", desc: "Drills, saws, grinders & more" },
  { id: "hand-tools", name: "Hand Tools", icon: "ri-tools-line", desc: "Wrenches, pliers, hammers" },
  { id: "measuring", name: "Measuring Tools", icon: "ri-ruler-line", desc: "Levels, tape measures, lasers" },
  { id: "fasteners", name: "Fasteners & Hardware", icon: "ri-settings-3-line", desc: "Bits, bolts, anchors" },
  { id: "safety", name: "Safety Equipment", icon: "ri-shield-check-line", desc: "PPE, hard hats, glasses" },
  { id: "storage", name: "Storage Solutions", icon: "ri-archive-line", desc: "Tool chests, cabinets, bags" },
];

interface NavbarProps {
  cartCount?: number;
}

const Navbar: React.FC<NavbarProps> = ({ cartCount = 0 }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const { totalItems } = useCart();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery("");
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white shadow-sm" : "bg-transparent"
      }`}
    >
      {/* Top bar */}
      <div className={`border-b transition-all duration-300 ${scrolled ? "border-gray-100 bg-gray-50" : "border-white/10 bg-black/20"}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-1.5 flex items-center justify-between text-xs">
          <span className={`${scrolled ? "text-gray-500" : "text-white/80"}`}>
            <i className="ri-map-pin-line mr-1"></i>
            3537 E Yale St, Phoenix, AZ 85008
          </span>
          <div className={`flex items-center gap-4 ${scrolled ? "text-gray-500" : "text-white/80"}`}>
            <a href="mailto:strayloftllc@outlook.com" className="hover:opacity-80 transition-opacity">
              <i className="ri-mail-line mr-1"></i>strayloftllc@outlook.com
            </a>
            <span className="hidden sm:inline">|</span>
            <span className="hidden sm:inline"><i className="ri-truck-line mr-1"></i>Free Shipping on Orders $200+</span>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <nav className={`transition-all duration-300 ${scrolled ? "bg-white" : "bg-transparent"}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 flex-shrink-0">
              <img
                src="https://public.readdy.ai/ai/img_res/4137faf5-b286-4eb1-a7fc-7d3899999f96.png"
                alt="STRAY LOFT LLC"
                className="h-10 w-auto object-contain"
              />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-1">
              <Link
                to="/"
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                  scrolled ? "text-gray-700 hover:text-orange-500 hover:bg-orange-50" : "text-white hover:text-orange-300"
                }`}
              >
                Home
              </Link>

              {/* Products Dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setProductsOpen(true)}
                onMouseLeave={() => setProductsOpen(false)}
              >
                <Link
                  to="/products"
                  className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                    scrolled ? "text-gray-700 hover:text-orange-500 hover:bg-orange-50" : "text-white hover:text-orange-300"
                  }`}
                >
                  Products
                  <i className="ri-arrow-down-s-line text-xs"></i>
                </Link>
                {productsOpen && (
                  <div className="absolute top-full left-0 mt-1 w-[600px] bg-white rounded-xl shadow-xl border border-gray-100 p-4 grid grid-cols-3 gap-2">
                    {productCategories.map((cat) => (
                      <Link
                        key={cat.id}
                        to={`/products?category=${cat.id}`}
                        className="flex items-start gap-3 p-3 rounded-lg hover:bg-orange-50 transition-colors group cursor-pointer"
                      >
                        <div className="w-8 h-8 flex items-center justify-center bg-orange-100 rounded-lg group-hover:bg-orange-200 transition-colors flex-shrink-0">
                          <i className={`${cat.icon} text-orange-600 text-sm`}></i>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-800 group-hover:text-orange-600 transition-colors">{cat.name}</div>
                          <div className="text-xs text-gray-500 mt-0.5">{cat.desc}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <Link
                to="/about"
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                  scrolled ? "text-gray-700 hover:text-orange-500 hover:bg-orange-50" : "text-white hover:text-orange-300"
                }`}
              >
                About Us
              </Link>
              <Link
                to="/contact"
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                  scrolled ? "text-gray-700 hover:text-orange-500 hover:bg-orange-50" : "text-white hover:text-orange-300"
                }`}
              >
                Contact
              </Link>
              <Link
                to="/contact"
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                  scrolled ? "text-gray-700 hover:text-orange-500 hover:bg-orange-50" : "text-white hover:text-orange-300"
                }`}
              >
                Support
              </Link>
            </div>

            {/* Right actions */}
            <div className="flex items-center gap-2">
              {/* Search */}
              <button
                onClick={() => setSearchOpen(!searchOpen)}
                className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                  scrolled ? "text-gray-600 hover:bg-gray-100" : "text-white hover:bg-white/10"
                }`}
              >
                <i className="ri-search-line text-lg"></i>
              </button>

              {/* Cart */}
              <Link
                to="/cart"
                className={`relative w-9 h-9 flex items-center justify-center rounded-lg transition-colors ${
                  scrolled ? "text-gray-600 hover:bg-gray-100" : "text-white hover:bg-white/10"
                }`}
              >
                <i className="ri-shopping-cart-line text-lg"></i>
                {totalItems > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-orange-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                    {totalItems > 9 ? "9+" : totalItems}
                  </span>
                )}
              </Link>

              {/* Mobile menu */}
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                  scrolled ? "text-gray-600 hover:bg-gray-100" : "text-white hover:bg-white/10"
                }`}
              >
                <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
              </button>
            </div>
          </div>

          {/* Search bar */}
          {searchOpen && (
            <div className="pb-3">
              <form onSubmit={handleSearch} className="flex gap-2">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search for tools, brands, categories..."
                  className="flex-1 px-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 bg-white text-gray-800"
                  autoFocus
                />
                <button
                  type="submit"
                  className="px-4 py-2 bg-orange-500 text-white text-sm rounded-lg hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Search
                </button>
              </form>
            </div>
          )}
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-4 py-4 space-y-1">
            <Link to="/" className="block px-3 py-2 text-sm font-medium text-gray-700 hover:text-orange-500 hover:bg-orange-50 rounded-lg" onClick={() => setMobileOpen(false)}>Home</Link>
            <Link to="/products" className="block px-3 py-2 text-sm font-medium text-gray-700 hover:text-orange-500 hover:bg-orange-50 rounded-lg" onClick={() => setMobileOpen(false)}>All Products</Link>
            {productCategories.map((cat) => (
              <Link key={cat.id} to={`/products?category=${cat.id}`} className="block px-3 py-2 pl-6 text-sm text-gray-600 hover:text-orange-500 hover:bg-orange-50 rounded-lg" onClick={() => setMobileOpen(false)}>
                <i className={`${cat.icon} mr-2`}></i>{cat.name}
              </Link>
            ))}
            <Link to="/about" className="block px-3 py-2 text-sm font-medium text-gray-700 hover:text-orange-500 hover:bg-orange-50 rounded-lg" onClick={() => setMobileOpen(false)}>About Us</Link>
            <Link to="/contact" className="block px-3 py-2 text-sm font-medium text-gray-700 hover:text-orange-500 hover:bg-orange-50 rounded-lg" onClick={() => setMobileOpen(false)}>Contact</Link>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Navbar;
